import { clientLogger } from '@/lib/clientLogger';
import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Edit2, Check, X } from 'lucide-react';
import { CustomSelect } from './CustomSelect';
import { LoadingSpinner } from './LoadingSpinner';
import { NumericInput } from './NumericInput';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import { gentleDateTimePickerRootSx, gentleDateTimePickerSlotProps, usePickerValue } from './gentleDateTimePicker';
import { formatInstantInCompanyTimeZone, normalizeBackendUtcIsoString, formatDateOnlyInCompanyTimeZone } from '@/lib/dateUtils';
import { PhoneInput } from './PhoneInput';
import { formatPhoneNumber } from '@/lib/phoneUtils';

interface Option {
    value: string | number;
    label: string;
    color?: string;
}

interface EditableFieldProps {
    label?: string;
    value: any;
    onSave: (value: any) => Promise<void>;
    type?: 'text' | 'number' | 'select' | 'date' | 'datetime-local' | 'textarea' | 'boolean' | 'phone';
    options?: Option[]; // For select type
    renderValue?: (value: any) => React.ReactNode; // Custom renderer for display mode
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    validate?: (value: any) => string | null; // Return error message or null
    inline?: boolean; // If true, edit button is inline with value instead of with label
    alwaysShowEdit?: boolean; // If true, edit icon is always visible (for mobile)
    /** IANA zone for datetime-local picker + display (e.g. company timezone). Matches MUI DateTimePicker `timezone`. */
    timezone?: string;
    selectFooterAction?: {
        label: string;
        onClick: () => void;
    };
}

export function EditableField({
    label,
    value,
    onSave,
    type = 'text',
    options = [],
    renderValue,
    placeholder,
    className = '',
    disabled = false,
    validate,
    inline = false,
    alwaysShowEdit = false,
    timezone,
    selectFooterAction,
}: EditableFieldProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [currentValue, setCurrentValue] = useState(value);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
    const pickerValue = usePickerValue(type === 'datetime-local' && currentValue ? normalizeBackendUtcIsoString(String(currentValue)) : null);

    useEffect(() => {
        setCurrentValue(value);
    }, [value]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isEditing]);

    const handleSave = async () => {
        if (validate) {
            const validationError = validate(currentValue);
            if (validationError) {
                setError(validationError);
                return;
            }
        }

        setIsSaving(true);
        try {
            await onSave(currentValue);
            setIsEditing(false);
            setError(null);
        } catch (err) {
            clientLogger.error("Failed to save field", err);
            // Optionally set an error state here to show to the user
            setError("Failed to save. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleAutoSave = async (newValue: any) => {
        // If the value hasn't changed, just exit edit mode without saving
        if (String(newValue) === String(value)) {
            setIsEditing(false);
            return;
        }

        if (validate) {
            const validationError = validate(newValue);
            if (validationError) {
                setError(validationError);
                return;
            }
        }

        setCurrentValue(newValue);
        setIsSaving(true);
        try {
            await onSave(newValue);
            setIsEditing(false);
            setError(null);
        } catch (err) {
            clientLogger.error("Failed to save field", err);
            setError("Failed to save. Please try again.");
            setCurrentValue(value); // Revert on failure
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setCurrentValue(value);
        setError(null);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && type !== 'textarea') {
            handleSave();
        } else if (e.key === 'Escape') {
            handleCancel();
        }
    };

    const renderInput = () => {
        switch (type) {
            case 'select':
                return (
                    <CustomSelect
                        value={String(currentValue)}
                        onChange={(val) => handleAutoSave(val)}
                        options={options.map(o => ({ ...o, value: String(o.value) }))}
                        placeholder={placeholder}
                        disabled={isSaving}
                        autoOpen={true}
                        footerAction={selectFooterAction}
                    />
                );
            case 'boolean':
                return (
                    <CustomSelect
                        value={String(currentValue)}
                        onChange={(val) => handleAutoSave(val === 'true')}
                        options={[
                            { value: 'true', label: 'Yes' },
                            { value: 'false', label: 'No' },
                        ]}
                        placeholder={placeholder}
                        disabled={isSaving}
                        autoOpen={true}
                    />
                );
            case 'textarea':
                return (
                    <textarea
                        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                        value={currentValue || ''}
                        onChange={(e) => setCurrentValue(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px]"
                        placeholder={placeholder}
                        disabled={isSaving}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') handleCancel();
                            // Allow Enter for newlines in textarea
                        }}
                    />
                );
            case 'number':
                return (
                    <NumericInput
                        value={currentValue === '' ? undefined : currentValue}
                        onChange={(val) => setCurrentValue(val)}
                        onKeyDown={handleKeyDown}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder={placeholder}
                        disabled={isSaving}
                    />
                );
            case 'datetime-local': {
                return (
                    <DateTimePicker
                        {...(timezone?.trim() ? { timezone: timezone.trim() } : {})}
                        value={pickerValue}
                        onChange={(newValue) => {
                            if (newValue) {
                                setCurrentValue(newValue.toISOString());
                            } else {
                                setCurrentValue('');
                            }
                        }}
                        sx={gentleDateTimePickerRootSx}
                        viewRenderers={{ hours: renderTimeViewClock, minutes: renderTimeViewClock, seconds: renderTimeViewClock }}
                        slotProps={{
                            ...gentleDateTimePickerSlotProps,
                            toolbar: { hidden: false, ...gentleDateTimePickerSlotProps.toolbar },
                            tabs: { hidden: false, ...gentleDateTimePickerSlotProps.tabs },
                        }}
                    />
                );
            }
            case 'phone':
                return (
                    <PhoneInput
                        value={currentValue || ''}
                        onChange={(val) => setCurrentValue(val)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder={placeholder}
                        disabled={isSaving}
                    />
                );
            default:
                return (
                    <input
                        ref={inputRef as React.RefObject<HTMLInputElement>}
                        type={type}
                        value={currentValue || ''}
                        onChange={(e) => setCurrentValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder={placeholder}
                        disabled={isSaving}
                    />
                );
        }
    };

    const defaultRenderValue = () => {
        if (value === null || value === undefined || value === '') return <span className="text-gray-400 italic">Empty</span>;

        if (type === 'select') {
            const option = options.find(o => o.value == value);
            if (option?.color) {
                // For status-like badges
                return (
                    <span className="px-2 py-1 text-xs font-medium rounded-full" style={{ backgroundColor: option.color, color: getContrastColor(option.color) }}>
                        {option.label}
                    </span>
                );
            }
            return option ? option.label : value;
        }
        if (type === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        if (type === 'datetime-local') {
            return formatInstantInCompanyTimeZone(value, timezone);
        }
        if (type === 'date') {
            return formatDateOnlyInCompanyTimeZone(value, timezone);
        }
        if (type === 'phone') {
            return formatPhoneNumber(String(value));
        }

        return value;
    };

    // Helper for text contrast
    const getContrastColor = (hexcolor: string) => {
        // If no color provided, return black
        if (!hexcolor) return '#000000';

        // Remove hash if present
        const hex = hexcolor.replace('#', '');

        // Convert to RGB
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);

        // Calculate YIQ brightness
        const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;

        // Return black or white
        return (yiq >= 128) ? '#000000' : '#FFFFFF';
    };

    if (isEditing) {
        return (
            <div className={`space-y-2 ${className}`}>
                {label && <label className="text-sm font-medium text-gray-700 block">{label}</label>}
                <div className="flex gap-2 items-start">
                    <div className="flex-1">
                        {renderInput()}
                        {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
                    </div>
                    {/* For select/boolean, we don't need Check/Cancel because selecting a value auto-saves. User can click outside to close the dropdown and then cancel the field, but adding auto-save means just selecting it is enough. We'll leave the Cancel button so they can still explicitly close the field if they didn't pick anything */}
                    {type !== 'select' && type !== 'boolean' && (
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Save"
                        >
                            {isSaving ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
                        </button>
                    )}
                    <button
                        onClick={handleCancel}
                        disabled={isSaving}
                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Cancel"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>
        );
    }

    // If inline mode or no label, put edit button inline with value
    const showInlineEdit = inline || !label;

    return (
        <div className={`group ${showInlineEdit ? 'inline-flex items-center gap-1' : 'relative'} ${className}`}>
            {label && !inline && (
                <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-gray-700">{label}</span>
                    {!disabled && !showInlineEdit && (
                        <button
                            onClick={() => setIsEditing(true)}
                            className={`${alwaysShowEdit ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all duration-200`}
                            title="Edit"
                        >
                            <Edit2 className="h-3 w-3" />
                        </button>
                    )}
                </div>
            )}
            <div
                className={`flex items-center gap-1 ${showInlineEdit ? '' : 'min-h-[24px]'} cursor-pointer`}
                onClick={() => !disabled && setIsEditing(true)}
            >
                <div className={`text-sm text-gray-900 ${showInlineEdit ? '' : 'break-words flex-1'}`}>
                    {renderValue ? renderValue(value) : defaultRenderValue()}
                </div>
                {!disabled && showInlineEdit && (
                    <button
                        onClick={() => setIsEditing(true)}
                        className={`${alwaysShowEdit ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all duration-200 flex-shrink-0`}
                        title="Edit"
                    >
                        <Edit2 className="h-3 w-3" />
                    </button>
                )}
            </div>
        </div>
    );
}
